import { useEffect, useState, type AnimationEvent } from "react";

/** The close animation's length; matches `--animate-modal-panel-out` in theme.css. */
export const MODAL_EXIT_MS = 150;

export interface ModalPresence {
  /** Render the dialog while true: it is open, or its exit is playing. */
  mounted: boolean;
  /** The exit is playing: the shell sets `inert`, `aria-hidden` and the exit classes. */
  closing: boolean;
  /** Attach to the panel; its own `animationend` ends the exit ahead of the timer. */
  onAnimationEnd: (e: AnimationEvent<HTMLElement>) => void;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * Keeps a dialog mounted through its close animation.
 *
 * `closing` is derived from the previous `open` during render rather than in
 * an effect, so the first closed render already carries the exit classes
 * instead of painting one unanimated frame. Under "reduce motion" the close
 * is instant. A closing dialog is `inert` at once, since inert content leaves
 * the accessibility tree: it has already returned focus and ignores keys and
 * clicks while it fades. jsdom fires no `animationend`, so a timer of the
 * same length also ends the exit; reopening mid-exit cancels it.
 */
export function useModalPresence(open: boolean): ModalPresence {
  const [closing, setClosing] = useState(false);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    setClosing(!open && !prefersReducedMotion());
  }

  useEffect(() => {
    if (!closing) return;
    const timer = setTimeout(() => setClosing(false), MODAL_EXIT_MS);
    return () => clearTimeout(timer);
  }, [closing]);

  return {
    mounted: open || closing,
    closing,
    onAnimationEnd: (e) => {
      // Only the panel's own animation counts; one bubbling up from a child doesn't.
      if (e.target === e.currentTarget && !open) setClosing(false);
    },
  };
}
